import { useState, useEffect, useCallback } from "react";
import { generateClient } from "aws-amplify/data";
import { getSubscriptionDetails } from "../utils/subscription";
import { getCachedUserAttributes, invalidateUserAttributesCache } from "../utils/userAttributesCache";
import { useUserAttributes } from "../contexts/UserAttributesContext";
import type { Schema } from "@backend-schema";
import type {
  SubscriptionSource,
  CompanySubscription,
} from "../types/subscription";

// Create a global event for subscription changes
export const SUBSCRIPTION_CHANGED_EVENT = "subscription_changed";

// Create a global event for company email verification changes
export const COMPANY_EMAIL_CHANGED_EVENT = "company_email_changed";

// Re-export so existing imports from this file continue to work
export { getCachedUserAttributes, invalidateUserAttributesCache };

/**
 * Hook to check and manage subscription status
 */
export function useSubscription() {
  const { attributes: contextAttributes, refresh: refreshAttributes } = useUserAttributes();
  const [subscriptionTier, setSubscriptionTier] = useState<"free" | "pro">(
    "free",
  );
  const [subscriptionDetails, setSubscriptionDetails] = useState<{
    tier: "free" | "pro";
    status?: string;
    trialEnd?: string;
  } | null>(null);
  const [subscriptionSource, setSubscriptionSource] =
    useState<SubscriptionSource>(null);
  const [companyDomain, setCompanyDomain] = useState<string | null>(null);
  const [companySubscription, setCompanySubscription] =
    useState<CompanySubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Function to check company subscription
  const checkCompanySubscription = useCallback(async (): Promise<{
    hasCompanySubscription: boolean;
    companyDomain: string | null;
    companySubscription: CompanySubscription | null;
  }> => {
    try {
      // Use context attributes if available (already fetched once at app level),
      // fall back to cache for any edge cases outside the provider
      const attributes = contextAttributes ?? await getCachedUserAttributes();
      // companyEmail available for future use (e.g., display in UI)
      const companyEmailVerified =
        attributes["custom:company_verified"] === "true";
      const domain = attributes["custom:company_domain"];

      // If no verified company email, return early
      if (!companyEmailVerified || !domain) {
        return {
          hasCompanySubscription: false,
          companyDomain: null,
          companySubscription: null,
        };
      }

      // Query CompanySubscription model
      const client = generateClient<Schema>();
      const result = await client.models.CompanySubscription.get({
        companyDomain: domain,
      });

      // Check subscription status and expiration
      const subscription = result.data;
      const isStatusActive = subscription?.subscriptionStatus === "active";

      // Check if subscription has expired
      let isNotExpired = true;
      if (subscription?.currentPeriodEnd) {
        const periodEnd = new Date(subscription.currentPeriodEnd);
        const now = new Date();
        isNotExpired = periodEnd > now;
      }

      // Check if user has been revoked from company subscription
      let isNotRevoked = true;
      if (subscription) {
        try {
          const { getCurrentUser } = await import("aws-amplify/auth");
          const user = await getCurrentUser();

          const revokedCheck = await client.models.RevokedCompanyEmployee.list({
            filter: {
              companyDomain: { eq: domain },
              userId: { eq: user.userId },
            },
          });

          isNotRevoked = !revokedCheck.data || revokedCheck.data.length === 0;
        } catch (err) {
          console.error("Error checking revocation status:", err);
          // If check fails, assume not revoked (fail open)
        }
      }

      // Subscription is valid if status is active AND not expired AND not revoked
      const hasActiveSubscription =
        isStatusActive && isNotExpired && isNotRevoked;

      return {
        hasCompanySubscription: hasActiveSubscription,
        companyDomain: domain,
        companySubscription: hasActiveSubscription ? subscription : null,
      };
    } catch (err) {
      console.error("Error checking company subscription:", err);
      return {
        hasCompanySubscription: false,
        companyDomain: null,
        companySubscription: null,
      };
    }
  }, []); // contextAttributes is read inside the async fn — no need as dep, avoids infinite loop

  // Function to check subscription status
  const checkSubscription = useCallback(async () => {
    try {
      setLoading(true);

      // Guard: verify session is valid before making AppSync calls
      // This prevents UserUnAuthenticatedException when SubscriptionProvider
      // mounts before Cognito tokens are fully established after sign-in
      const { getCachedAuthSession } = await import('../utils/userAttributesCache');
      const session = await getCachedAuthSession();
      if (!session.tokens?.idToken) {
        setLoading(false);
        return;
      }

      // Check individual subscription (existing logic)
      const individualDetails = await getSubscriptionDetails();
      const hasIndividualSubscription = individualDetails.tier === "pro";

      // Check company subscription (new logic)
      const {
        hasCompanySubscription,
        companyDomain: domain,
        companySubscription: companySub,
      } = await checkCompanySubscription();

      // Grant Pro if either is active
      const isPro = hasIndividualSubscription || hasCompanySubscription;
      const tier = isPro ? "pro" : "free";

      // Determine subscription source
      let source: SubscriptionSource = null;
      if (hasIndividualSubscription) {
        source = "individual";
      } else if (hasCompanySubscription) {
        source = "company";
      }

      setSubscriptionTier(tier);
      setSubscriptionDetails(individualDetails);
      setSubscriptionSource(source);
      setCompanyDomain(domain);
      setCompanySubscription(companySub);
      setError(null);
    } catch {
      setError("Failed to check subscription status");
      setSubscriptionTier("free");
      setSubscriptionSource(null);
      setCompanyDomain(null);
      setCompanySubscription(null);
    } finally {
      setLoading(false);
    }
  }, [checkCompanySubscription]);

  // Listen for subscription change events
  useEffect(() => {
    const handleSubscriptionChange = () => {
      checkSubscription();
    };

    const handleCompanyEmailChange = () => {
      checkSubscription();
    };

    // Add event listeners
    window.addEventListener(
      SUBSCRIPTION_CHANGED_EVENT,
      handleSubscriptionChange,
    );
    window.addEventListener(
      COMPANY_EMAIL_CHANGED_EVENT,
      handleCompanyEmailChange,
    );

    // Initial check
    checkSubscription();

    // Cleanup
    return () => {
      window.removeEventListener(
        SUBSCRIPTION_CHANGED_EVENT,
        handleSubscriptionChange,
      );
      window.removeEventListener(
        COMPANY_EMAIL_CHANGED_EVENT,
        handleCompanyEmailChange,
      );
    };
  }, [checkSubscription]);

  // Function to refresh subscription status
  const refreshSubscription = useCallback(() => {
    invalidateUserAttributesCache();
    refreshAttributes(); // Refresh context attributes too
    checkSubscription();
  }, [checkSubscription, refreshAttributes]);

  // Function to notify all components about subscription changes
  const notifySubscriptionChanged = useCallback(() => {
    window.dispatchEvent(new Event(SUBSCRIPTION_CHANGED_EVENT));
  }, []);

  const isPro = subscriptionTier === "pro";

  // Function to get upgrade prompt message
  const getUpgradeMessage = useCallback(() => {
    // Show different message if user has company email
    if (companyDomain) {
      return "Your company does not have an active subscription. Upgrade to Pro or ask your admin to purchase a company subscription.";
    }
    return "Upgrade to Pro to access this feature.";
  }, [companyDomain]);

  return {
    subscriptionTier,
    subscriptionDetails,
    subscriptionSource,
    companyDomain,
    companySubscription,
    isPro,
    loading,
    error,
    refreshSubscription,
    notifySubscriptionChanged,
    getUpgradeMessage,
  };
}
